import { Info as InfoIcon } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { getSGRWarrantyRequestFile } from 'lib/api/sgr';
import AlertBanner from 'lib/designSystem/AlertBanner';
import { Warranty } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface FileProps {
  warranty: Warranty;
  skeletonHeight?: number;
}

const File: React.FC<FileProps> = ({ warranty, skeletonHeight = 600 }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const notActiveStates = ['REJECTED', 'CANCELLED', 'CLAIMED'];

  const loadPdf = async () => {
    try {
      if (notActiveStates.includes(warranty.status)) return;
      setLoading(true);
      setError(null);
      const pdfData = await getSGRWarrantyRequestFile(dispatch, warranty.id);

      if (!pdfData) throw new Error('No se pudo obtener el documento');

      const url = URL.createObjectURL(pdfData.blob);
      const viewerUrl = `${url}#pagemode=none`;

      setViewerUrl(viewerUrl);
      setBlobUrl(url);
    } catch (error) {
      console.error(error);
      setError(
        'Hubo un error al cargar el documento. Contacte al soporte técnico.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!notActiveStates.includes(warranty.status)) void loadPdf();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [warranty.id, warranty.status]);

  if (notActiveStates.includes(warranty.status)) {
    return (
      <AlertBanner
        severity="info"
        icon={<InfoIcon sx={{ fontSize: '1.5rem' }} />}
        title="Certificado de fianza no disponible"
        message="El certificado de fianza no está disponible porque la fianza se encuentra en un estado no activo, como siniestrada, cancelada o rechazada."
      />
    );
  }

  if (loading)
    return (
      <Skeleton
        variant="rectangular"
        animation="wave"
        width="100%"
        height={skeletonHeight}
        sx={{ borderRadius: 1 }}
      />
    );
  if (error || !viewerUrl)
    return <AlertBanner severity="error" title="Error" message={error ?? ''} />;
  return (
    <iframe
      src={viewerUrl}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        minHeight: '600px',
      }}
      title={`Documento de Garantía ${warranty.id}`}
    />
  );
};

export default File;
